import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Container,
  Header,
  SpaceBetween,
  ExpandableSection,
  Box,
  TextContent,
  Badge,
  Button,
  Alert,
  ButtonGroup,
  StatusIndicator,
  Tabs,
  Hotspot
} from '@cloudscape-design/components';
import ChatBubble from "@cloudscape-design/chat-components/chat-bubble";
import Avatar from "@cloudscape-design/chat-components/avatar";
import CitationPanel from '../../components/CitationPanel';
import CitationSidePanel from '../../components/CitationSidePanel';
import InsightsSection from '../../components/InsightsSection';
import WhatsNextSection from '../../components/WhatsNextSection';
import GuardrailsShowcase from '../../components/GuardrailsShowcase';
import FeedbackWidget from '../../components/FeedbackWidget';
import apiService from '../../services/api';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import { parsePageNumber } from '../../utils/citationHelpers';
import { useSectionTimer } from '../../hooks/useAnalytics';
import analyticsService from '../../services/analyticsService';
import resultsCache from '../../services/resultsCache';
import { HOTSPOT_IDS } from '../../context/TutorialContext';
import { getTableStyles } from '../../styles/tableStyles';
import { isNumericContent } from '../../utils/alignmentHeuristic';
import { useTheme } from '../../context/ThemeContext';

// Document mapping function to match citations to correct documents
const getDocumentInfo = (citationSource) => {
  if (!citationSource) {
    return {
      documentName: "View Amazon Annual Report 2024",
      documentPath: "/Amazon-2024-Annual-Report.pdf"
    };
  }

  const source = citationSource.toLowerCase();

  // Match patterns for Quarterly Report (2025)
  if (source.includes('quarterly') || 
      source.includes('q1') || 
      (source.includes('2025') && !source.includes('sustainability')) ||
      source.includes('quartely')) { // Handle potential typo in filename
    return {
      documentName: "View Amazon Q1 2025 Report",
      documentPath: "/Amazon-2025-Quartely-Report.pdf"
    };
  }
  
  // Match patterns for Sustainability Executive Summary
  if ((source.includes('sustainability') || source.includes('esg')) && 
      (source.includes('summary') || source.includes('executive'))) {
    return {
      documentName: "View Sustainability Summary 2024",
      documentPath: "/2024-sustainability-executive-summary.pdf"
    };
  }
  
  // Match patterns for full Sustainability Report
  if (source.includes('sustainability') || 
      source.includes('esg') || 
      source.includes('environmental') ||
      source.includes('social') ||
      source.includes('governance')) {
    return {
      documentName: "View Amazon Sustainability Report 2024",
      documentPath: "/2024-amazon-sustainability-report.pdf"
    };
  }

  // Match patterns for 2023 Annual Report (check before generic annual match)
  if (source.includes('2023') && (source.includes('annual') || source.includes('report'))) {
    return {
      documentName: "View Amazon Annual Report 2023",
      documentPath: "/Amazon-2024-Annual-Report.pdf"  // Same PDF — 2023 data is in the 2024 filing
    };
  }
  
  // Match patterns for Annual Report (most common, so check last)
  if (source.includes('annual') || 
      source.includes('2024') ||
      source.includes('10-k') ||
      source.includes('form 10') ||
      source.includes('financial')) {
    return {
      documentName: "View Amazon Annual Report 2024",
      documentPath: "/Amazon-2024-Annual-Report.pdf"
    };
  }
  
  // Default to Annual Report for anything else (most common)
  return {
    documentName: "View Amazon Annual Report 2024",
    documentPath: "/Amazon-2024-Annual-Report.pdf"
  };
};

// Enhanced citation extraction function
const extractCitations = (documentResult, citationResult, result, questionText = '') => {
  const citations = [];
  const seenSources = new Set(); // Prevent duplicate citations

  // Helper function to normalize source names and add citation if not duplicate
  const normalizeSourceName = (source) => {
    if (!source) return 'Unknown Document';

    // Market data source — return as-is, already labelled
    if (source.includes('Market Data') || source.includes('Market Data Feed')) {
      return source;
    }

    // Remove S3 URLs and extract just the filename
    if (source.includes('s3://') || source.includes('.amazonaws.com/')) {
      const urlMatch = source.match(/([^\/]+\.pdf)$/i);
      if (urlMatch) {
        return urlMatch[1];
      }
    }

    // Clean up common prefixes and suffixes
    let cleanSource = source
      .replace(/^.*\//, '') // Remove path prefixes
      .replace(/\.pdf$/i, '') // Remove .pdf extension
      .replace(/^Amazon-?/i, 'Amazon ') // Normalize Amazon prefix
      .replace(/-/g, ' ') // Replace hyphens with spaces
      .replace(/\s+/g, ' ') // Normalize whitespace
      .trim();

    // Add back .pdf if it was a PDF file
    if (source.toLowerCase().includes('.pdf')) {
      cleanSource += '.pdf';
    }

    return cleanSource || 'Unknown Document';
  };

  const addCitation = (citation) => {
    // Normalize the source name
    citation.source = normalizeSourceName(citation.source);

    const key = `${citation.source}-${citation.page}`;
    if (!seenSources.has(key)) {
      seenSources.add(key);
      citations.push(citation);
    }
  };

  // ── Primary: Use raw KB sources when available (verbatim text from PDF) ──
  const kbSources = result.kb_sources || result.research_documents?.kb_sources;
  if (kbSources && Array.isArray(kbSources) && kbSources.length > 0) {
    const questionLower = questionText.toLowerCase();
    const questionWords = questionLower.split(/\s+/).filter(w => w.length > 3);

    // #1: Filter sources relevant to this question by matching query text against question
    const scored = kbSources.map(src => {
      const queryLower = (src.query || '').toLowerCase();
      // Score: how many question keywords appear in the KB query
      const matchCount = questionWords.filter(w => queryLower.includes(w)).length;
      const relevance = questionWords.length > 0 ? matchCount / questionWords.length : 0;
      return { ...src, _relevance: relevance };
    });

    // Keep sources with >30% keyword overlap, or all if none match
    let relevant = scored.filter(s => s._relevance > 0.3);
    if (relevant.length === 0) relevant = scored;

    // Sort by relevance then score
    relevant.sort((a, b) => (b._relevance - a._relevance) || (b.score - a.score));

    // #2: Deduplicate by content (first 100 chars) not just source+page
    const seenContent = new Set();

    for (const src of relevant) {
      const text = (src.text || '').trim();
      if (!text) continue;

      // Content-based dedup
      const contentKey = text.substring(0, 100).toLowerCase().replace(/\s+/g, ' ');
      if (seenContent.has(contentKey)) continue;
      seenContent.add(contentKey);

      const pageNum = src.page != null && src.page !== 'Unknown'
        ? `p. ${String(src.page).replace(/\.0$/, '')}`
        : 'p. N/A';
      const score = typeof src.score === 'number' ? Math.round(src.score * 100) : 75;

      // #6: Smart text preview — find the most relevant sentence
      let displayText = text;
      if (text.length > 200 && questionWords.length > 0) {
        const sentences = text.split(/[.!?]+/).filter(s => s.trim().length > 20);
        const bestSentence = sentences.reduce((best, sentence) => {
          const sLower = sentence.toLowerCase();
          const hits = questionWords.filter(w => sLower.includes(w)).length;
          return hits > best.hits ? { text: sentence.trim(), hits } : best;
        }, { text: '', hits: -1 });
        if (bestSentence.hits > 0) {
          displayText = bestSentence.text;
        } else {
          displayText = text.substring(0, 200);
        }
      }

      addCitation({
        source: src.source_uri || 'Unknown Document',
        page: pageNum,
        confidence: Math.min(score, 100),
        text: displayText,
        fullText: text,  // Full KB chunk for PDF highlighting
        type: 'knowledge_base',
      });
    }

    if (citations.length > 0) {
      return citations.sort((a, b) => b.confidence - a.confidence);
    }
  }

  // Pattern 1: Standard "Source: ... Page: ..." format
  const extractStandardFormat = (text) => {
    if (!text) return;

    const sourceMatches = text.match(/Source:\s*([^,\n]+)(?:,?\s*Page:\s*([0-9.]+))?/gi);
    if (sourceMatches) {
      sourceMatches.forEach(match => {
        const sourceMatch = match.match(/Source:\s*([^,\n]+)/i);
        const pageMatch = match.match(/Page:\s*([0-9.]+)/i);

        if (sourceMatch) {
          // Extract quote/context from surrounding text - improved logic
          const sourceIndex = text.indexOf(match);
          let context = 'Supporting evidence found';

          if (sourceIndex > 0) {
            // Look for the sentence or paragraph before the source attribution
            const beforeText = text.substring(Math.max(0, sourceIndex - 300), sourceIndex);

            // Try to find a complete sentence before the source
            const sentenceMatch = beforeText.match(/([^.!?]*[.!?])\s*$/);
            if (sentenceMatch && sentenceMatch[1].trim().length > 10) {
              context = sentenceMatch[1].trim();
            } else {
              // Fallback: get the last meaningful chunk of text
              const lines = beforeText.split('\n').filter(line => line.trim().length > 5);
              if (lines.length > 0) {
                const lastLine = lines[lines.length - 1].trim();
                // Clean up any markdown or formatting
                context = lastLine.replace(/^\*+\s*/, '').replace(/\*+$/, '').trim();
                if (context.length < 10) {
                  context = 'Referenced information from source document';
                }
              }
            }
          }

          // Ensure context is meaningful and not just formatting characters
          if (context.match(/^[-\[\]{}()*+?.,\\^$|#\s]*$/)) {
            context = 'Information extracted from source document';
          }

          addCitation({
            source: sourceMatch[1].trim(),
            page: pageMatch ? `p. ${pageMatch[1]}` : 'p. N/A',
            confidence: 85,
            text: context,
            type: 'document_reference'
          });
        }
      });
    }
  };

  // Pattern 2: Inline citations like "(Amazon 2024 Annual Report, p. 15)"
  const extractInlineFormat = (text) => {
    if (!text) return;

    const inlineMatches = text.match(/\(([^)]*(?:report|document|filing)[^)]*,?\s*p\.?\s*[0-9]+[^)]*)\)/gi);
    if (inlineMatches) {
      inlineMatches.forEach(match => {
        const cleanMatch = match.replace(/[()]/g, '');
        const parts = cleanMatch.split(',');
        const sourcePart = parts[0]?.trim();
        const pagePart = parts.find(part => part.match(/p\.?\s*[0-9]+/i));

        if (sourcePart && pagePart) {
          const pageNumber = pagePart.match(/([0-9]+)/)?.[1];

          // Try to extract context around the inline citation
          const citationIndex = text.indexOf(match);
          let context = 'Referenced in analysis';

          if (citationIndex > 0) {
            // Get the sentence containing the citation
            const beforeText = text.substring(Math.max(0, citationIndex - 150), citationIndex);
            const afterText = text.substring(citationIndex + match.length, Math.min(text.length, citationIndex + match.length + 50));

            // Find the sentence boundaries
            const sentenceStart = beforeText.lastIndexOf('.') + 1;
            const sentenceEnd = afterText.indexOf('.');

            if (sentenceStart >= 0) {
              const beforePart = beforeText.substring(sentenceStart).trim();
              const afterPart = sentenceEnd >= 0 ? afterText.substring(0, sentenceEnd) : afterText;
              const fullSentence = (beforePart + ' ' + afterPart).trim();

              if (fullSentence.length > 10 && !fullSentence.match(/^[-\[\]{}()*+?.,\\^$|#\s]*$/)) {
                context = fullSentence;
              }
            }
          }

          addCitation({
            source: sourcePart,
            page: pageNumber ? `p. ${pageNumber}` : 'p. N/A',
            confidence: 80,
            text: context,
            type: 'inline_citation'
          });
        }
      });
    }
  };

  // Pattern 3: Reference sections with "**Source Attribution:**" or similar
  const extractReferenceSection = (text) => {
    if (!text) return;

    const refSectionMatch = text.match(/\*\*(?:Source|Reference|Citation).*?\*\*:?\s*([\s\S]*?)(?=\n\n|\*\*|$)/i);
    if (refSectionMatch) {
      const refText = refSectionMatch[1];

      // Look for document names and page numbers in reference section
      const docMatches = refText.match(/([^,\n]+(?:report|document|filing)[^,\n]*)/gi);
      if (docMatches) {
        docMatches.forEach(docMatch => {
          const pageMatch = refText.match(new RegExp(`${docMatch.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}[^\\n]*?p\\.?\\s*([0-9]+)`, 'i'));

          addCitation({
            source: docMatch.trim(),
            page: pageMatch ? `p. ${pageMatch[1]}` : 'p. N/A',
            confidence: 90,
            text: 'Detailed in source document',
            type: 'reference_section'
          });
        });
      }
    }
  };

  // Pattern 4: Bracket citations like "[Amazon 2024 Annual Report, p. 15]"
  const extractBracketFormat = (text) => {
    if (!text) return;

    const bracketMatches = text.match(/\[([^\]]*(?:report|document|filing|annual)[^\]]*(?:p\.?\s*[0-9]+)?[^\]]*)\]/gi);
    if (bracketMatches) {
      bracketMatches.forEach(match => {
        const cleanMatch = match.replace(/[\[\]]/g, '');
        const parts = cleanMatch.split(',');
        const sourcePart = parts[0]?.trim();
        const pagePart = parts.find(part => part.match(/p\.?\s*[0-9]+/i));

        if (sourcePart) {
          const pageNumber = pagePart ? pagePart.match(/([0-9]+)/)?.[1] : null;

          // Extract context around the bracket citation
          const citationIndex = text.indexOf(match);
          let context = 'Referenced in analysis';

          if (citationIndex > 0) {
            // Look for the sentence or bullet point containing this citation
            const beforeText = text.substring(Math.max(0, citationIndex - 200), citationIndex);
            const afterText = text.substring(citationIndex + match.length, Math.min(text.length, citationIndex + match.length + 100));

            // Try to find a complete sentence or bullet point
            const sentenceMatch = beforeText.match(/([^.!?\n]*[.!?]?)\s*$/);
            if (sentenceMatch && sentenceMatch[1].trim().length > 15) {
              context = sentenceMatch[1].trim();
              // Add a bit of the following text if it makes sense
              const nextPart = afterText.split(/[.!?\n]/)[0];
              if (nextPart && nextPart.trim().length > 5 && nextPart.trim().length < 50) {
                context += nextPart.trim();
              }
            } else {
              // Look for bullet points or list items
              const bulletMatch = beforeText.match(/[-*•]\s*([^-*•\n]+)\s*$/);
              if (bulletMatch && bulletMatch[1].trim().length > 15) {
                context = bulletMatch[1].trim();
              }
            }

            // Clean up the context
            context = context
              .replace(/^\*+\s*/, '') // Remove markdown asterisks
              .replace(/\*+$/, '')
              .replace(/^[-*•]\s*/, '') // Remove bullet points
              .trim();
          }

          addCitation({
            source: sourcePart,
            page: pageNumber ? `p. ${pageNumber}` : 'p. N/A',
            confidence: 85,
            text: context,
            type: 'bracket_citation'
          });
        }
      });
    }
  };

  // Pattern 5: Simple document mentions with page numbers
  const extractSimpleDocumentMentions = (text) => {
    if (!text) return;

    // Look for patterns like "Amazon Annual Report page 15" or "2024 report, page 42"
    const simpleMatches = text.match(/((?:Amazon|annual|quarterly|report|filing)[^.!?]*?(?:page|p\.?)\s*[0-9]+)/gi);
    if (simpleMatches) {
      simpleMatches.forEach(match => {
        const pageMatch = match.match(/(?:page|p\.?)\s*([0-9]+)/i);
        const docMatch = match.match(/(.*?)(?:page|p\.?)/i);

        if (pageMatch && docMatch) {
          const sourceName = docMatch[1].trim();
          const pageNumber = pageMatch[1];

          // Get context around this mention
          const mentionIndex = text.indexOf(match);
          let context = 'Document reference';

          if (mentionIndex > 0) {
            const beforeText = text.substring(Math.max(0, mentionIndex - 80), mentionIndex);
            const afterText = text.substring(mentionIndex + match.length, Math.min(text.length, mentionIndex + match.length + 80));
            context = (beforeText + ' ' + afterText).trim();

            // Clean up the context
            if (context.length > 100) {
              context = context.substring(0, 100) + '...';
            }
          }

          addCitation({
            source: sourceName,
            page: `p. ${pageNumber}`,
            confidence: 75,
            text: context,
            type: 'simple_mention'
          });
        }
      });
    }
  };

  // Pattern 4: Knowledge base metadata (if available in result object)
  const extractKnowledgeBaseMetadata = (result) => {
    // Check if result has knowledge base metadata
    if (result.research_documents?.document_research?.metadata?.sources) {
      result.research_documents.document_research.metadata.sources.forEach(source => {
        addCitation({
          source: source.document_name || 'Knowledge Base Document',
          page: source.page_number ? `p. ${source.page_number}` : 'p. N/A',
          confidence: source.confidence || 95,
          text: source.excerpt || 'Knowledge base reference',
          type: 'knowledge_base'
        });
      });
    }
  };

  // Apply all extraction patterns, prioritizing citation_tracking result
  // Citation tracking result should have better formatted citations
  if (citationResult) {
    extractStandardFormat(citationResult);
    extractInlineFormat(citationResult);
    extractReferenceSection(citationResult);
    extractBracketFormat(citationResult);
    extractSimpleDocumentMentions(citationResult);
  }

  // Also check document result if citation result didn't yield enough citations
  if (documentResult && citations.length < 2) {
    extractStandardFormat(documentResult);
    extractInlineFormat(documentResult);
    extractReferenceSection(documentResult);
    extractBracketFormat(documentResult);
    extractSimpleDocumentMentions(documentResult);
  }

  extractKnowledgeBaseMetadata(result);

  // Conservative fallback: Only create fallback citations if absolutely no citations found
  // and we have substantial content that clearly references documents
  if (citations.length === 0 && (citationResult || documentResult)) {
    const textToAnalyze = citationResult || documentResult;

    // Only create fallback if the text clearly mentions documents/reports
    const hasDocumentReferences = textToAnalyze.match(/(report|document|filing|annual|quarterly|amazon)/i);

    if (hasDocumentReferences) {
      // Try to extract the most relevant sentence that mentions documents
      const sentences = textToAnalyze.split(/[.!?]+/).filter(s => s.trim().length > 20);
      let fallbackText = 'Information extracted from research analysis';

      // Look for sentences that mention documents or specific data
      const relevantSentence = sentences.find(s =>
        s.match(/(report|document|annual|revenue|million|billion|percent|\$)/i) &&
        s.trim().length > 25 &&
        !s.match(/^[-\[\]{}()*+?.,\\^$|#\s]*$/)
      );

      if (relevantSentence) {
        fallbackText = relevantSentence.trim()
          .replace(/^\*+\s*/, '')
          .replace(/\*+$/, '')
          .replace(/^[-\[\]{}()*+?.,\\^$|#\s]+/, '')
          .trim();
      }

      // Only add fallback if we have meaningful content
      if (fallbackText.length > 20) {
        addCitation({
          source: 'Amazon 2024 Annual Report',
          page: 'p. N/A',
          confidence: 60,
          text: fallbackText,
          type: 'fallback'
        });
      }
    }
  }

  // Final cleanup: ensure all citations have meaningful text and strip markdown
  citations.forEach(citation => {
    if (!citation.text || citation.text.match(/^[-\[\]{}()*+?.,\\^$|#\s]*$/) || citation.text.length < 3) {
      citation.text = 'Supporting information from source document';
    }
    // Strip markdown bold/italic markers so fuzzy match works against plain PDF text
    citation.text = citation.text
      .replace(/\*\*([^*]+)\*\*/g, '$1')  // **bold** → bold
      .replace(/\*([^*]+)\*/g, '$1')      // *italic* → italic
      .replace(/__([^_]+)__/g, '$1')      // __bold__ → bold
      .replace(/_([^_]+)_/g, '$1')        // _italic_ → italic
      .replace(/^#+\s*/gm, '')            // # headings → text
      .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1') // [link](url) → link
      .trim();
  });

  // ── Market Data: scan all text fields for [Source: Market Data] tags ──
  const allText = [
    documentResult, citationResult,
    result?.citation_tracking, result?.evidence_compilation
  ].filter(Boolean).join(' ');

  const marketDataRegex = /\[Source:\s*Market Data[^\]]*\]/gi;
  if (marketDataRegex.test(allText)) {
    const key = 'Market Data-market';
    if (!seenSources.has(key)) {
      seenSources.add(key);
      citations.push({
        source: 'Market Data — AMZN historical prices',
        page: 'Market Data Feed',
        confidence: 70,
        text: 'Historical AMZN share price data (2022–Q1 2025). For contextual commentary only — not investment advice.',
        type: 'market_data'
      });
    }
  }

  // Sort citations by confidence (highest first)
  return citations.sort((a, b) => b.confidence - a.confidence);
};

// Professional markdown renderer using react-markdown
/**
 * Build a citation index mapping each citation to a sequential number.
 * Returns an array of { sourceWords, page, num } for flexible matching.
 */
function buildCitationIndex(citations) {
  if (!citations || citations.length === 0) return [];
  return citations.map((c, i) => {
    // Extract meaningful words from the source name (strip .pdf, punctuation)
    const sourceWords = (c.source || '')
      .toLowerCase()
      .replace(/\.pdf$/i, '')
      .replace(/[-_]/g, ' ')
      .split(/\s+/)
      .filter(w => w.length > 1);
    // Extract page number digits
    const pageMatch = (c.page || '').match(/(\d+)/);
    const pageNum = pageMatch ? pageMatch[1] : '';
    return { sourceWords, pageNum, num: i + 1 };
  });
}

/**
 * Find the citation number for an inline reference string like
 * "Amazon 2024 Annual Report, p.1" or "Amazon-2024-Annual-Report.pdf, Page: 14"
 */
function findCitationNumber(refText, citationEntries) {
  if (!refText || citationEntries.length === 0) return null;
  const lower = refText.toLowerCase();

  // Extract page number from the inline ref
  const refPageMatch = lower.match(/(?:page|p\.?)\s*[:.]?\s*(\d+)/);
  const refPageNum = refPageMatch ? refPageMatch[1] : '';

  let bestMatch = null;
  let bestScore = 0;

  for (const entry of citationEntries) {
    // Count how many source words appear in the inline ref
    const wordHits = entry.sourceWords.filter(w => lower.includes(w)).length;
    const wordScore = entry.sourceWords.length > 0 ? wordHits / entry.sourceWords.length : 0;

    // Page match bonus
    const pageMatch = refPageNum && entry.pageNum && refPageNum === entry.pageNum;

    const score = wordScore + (pageMatch ? 0.5 : 0);

    if (score > bestScore && wordScore >= 0.4) {
      bestScore = score;
      bestMatch = entry.num;
    }
  }

  return bestMatch;
}

const renderMarkdown = (text, citations, onInlineCitationClick, isDark = false, isSummit = false) => {
  if (!text) return text;

  // Table styles for this theme/mode
  const ts = getTableStyles(isDark, isSummit);
  let rowIndex = 0;

  // Build citation index for numbered references
  const citationIndex = buildCitationIndex(citations);

  // Clean up the markdown text before processing
  let cleanText = text
    // Remove orphaned numbers and bullets that cause formatting issues
    .replace(/^\d+\.\s*$/gm, '')
    .replace(/^[-*•]\s*$/gm, '')
    // Fix broken numbered lists
    .replace(/(\d+\.\s*[^\n]*)\n(\d+)\n/g, '$1\n$2. ')
    // Clean up excessive whitespace
    .replace(/\n{3,}/g, '\n\n')
    .trim();

  // Replace verbose inline citations with numbered superscript refs
  // Matches bracket patterns containing document names with optional page refs
  if (citationIndex.length > 0) {
    // Match [text containing document keywords, possibly with page refs]
    // Also match (text containing document keywords) in parentheses
    cleanText = cleanText.replace(
      /\[([^\]]*?(?:Annual|Report|report|sustainability|Quarterly|quarterly|\.pdf)[^\]]*?)\](?:\([^)]*\))?/gi,
      (match, inner) => {
        const num = findCitationNumber(inner, citationIndex);
        if (num) return `<sup data-cite="${num}">[${num}]</sup>`;
        return match;
      }
    );
    // Handle parenthesized citations too: (Amazon 2024 Annual Report, p.1)
    cleanText = cleanText.replace(
      /\(([^)]*?(?:Annual|Report|report|sustainability|Quarterly|quarterly|\.pdf)[^)]*?,\s*(?:p\.?|Page)\s*[:.]?\s*[\d.]+[^)]*?)\)/gi,
      (match, inner) => {
        const num = findCitationNumber(inner, citationIndex);
        if (num) return `<sup data-cite="${num}">[${num}]</sup>`;
        return match;
      }
    );
    // Clean up standalone [V] markers
    cleanText = cleanText.replace(/\s*\[V\]/g, '');
  }

  return (
    <ReactMarkdown
      remarkPlugins={[remarkGfm]}
      rehypePlugins={[rehypeRaw]}
      components={{
        // Custom styling for headers
        h1: ({ children }) => (
          <h1 style={{
            margin: '24px 0 12px 0',
            fontWeight: 600,
            fontSize: '1.3em',
          }}>
            {children}
          </h1>
        ),
        h2: ({ children }) => (
          <h2 style={{
            margin: '20px 0 10px 0',
            fontWeight: 600,
            fontSize: '1.2em',
          }}>
            {children}
          </h2>
        ),
        h3: ({ children }) => (
          <h3 style={{
            margin: '16px 0 8px 0',
            fontWeight: 600,
            fontSize: '1.1em',
          }}>
            {children}
          </h3>
        ),

        // Custom styling for paragraphs
        p: ({ children }) => (
          <p style={{
            marginBottom: '14px',
            lineHeight: '1.6',
          }}>
            {children}
          </p>
        ),

        // Custom styling for ordered lists
        ol: ({ children }) => (
          <ol style={{
            marginBottom: '16px',
            paddingLeft: '20px',
            lineHeight: '1.6'
          }}>
            {children}
          </ol>
        ),

        // Custom styling for unordered lists
        ul: ({ children }) => (
          <ul style={{
            marginBottom: '16px',
            paddingLeft: '20px',
            lineHeight: '1.6'
          }}>
            {children}
          </ul>
        ),

        // Custom styling for list items
        li: ({ children }) => (
          <li style={{
            marginBottom: '8px',
          }}>
            {children}
          </li>
        ),

        // Custom styling for code blocks and inline code
        code: ({ inline, children, className }) => {
          if (inline) {
            return (
              <code style={{
                background: '#f0f0f0',
                padding: '2px 4px',
                borderRadius: '3px',
                fontFamily: 'Monaco, Menlo, "Ubuntu Mono", monospace',
                fontSize: '0.9em',
                color: '#d63384'
              }}>
                {children}
              </code>
            );
          }

          return (
            <pre style={{
              background: '#f5f5f5',
              padding: '12px',
              borderRadius: '4px',
              margin: '12px 0',
              overflowX: 'auto',
              fontFamily: 'Monaco, Menlo, "Ubuntu Mono", monospace',
              fontSize: '0.9em',
              border: '1px solid #e1e4e8'
            }}>
              <code className={className}>{children}</code>
            </pre>
          );
        },

        // Custom styling for blockquotes
        blockquote: ({ children }) => (
          <blockquote style={{
            borderLeft: '4px solid currentColor',
            margin: '12px 0',
            padding: '8px 16px',
            fontStyle: 'italic',
            opacity: 0.85
          }}>
            {children}
          </blockquote>
        ),

        // Custom styling for horizontal rules
        hr: () => (
          <hr style={{
            margin: '20px 0',
            border: 'none',
            borderTop: '1px solid #e1e4e8'
          }} />
        ),

        // Custom styling for links and citations
        a: ({ href, children }) => {
          // Check if this looks like a citation
          const childText = typeof children === 'string' ? children :
            Array.isArray(children) ? children.join('') : '';

          if (childText.includes('Report') || childText.includes('p.')) {
            const num = findCitationNumber(childText, citationIndex);
            if (num && onInlineCitationClick) {
              return (
                <sup
                  role="button"
                  tabIndex={0}
                  onClick={() => onInlineCitationClick(num)}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onInlineCitationClick(num); } }}
                  aria-label={`Citation ${num}`}
                  style={{
                    color: '#0073bb',
                    fontWeight: 600,
                    cursor: 'pointer',
                    fontSize: '0.75em',
                    verticalAlign: 'super',
                    padding: '0 1px',
                  }}
                >
                  [{num}]
                </sup>
              );
            }
            return (
              <span style={{
                color: '#0073bb',
                fontWeight: 500,
                background: '#f0f8ff',
                padding: '1px 4px',
                borderRadius: '3px',
                fontSize: '0.9em'
              }}>
                [{children}]
              </span>
            );
          }

          // Regular links
          return (
            <a
              href={href}
              style={{
                color: '#0073bb',
                textDecoration: 'none',
                borderBottom: '1px solid #0073bb'
              }}
              target="_blank"
              rel="noopener noreferrer"
            >
              {children}
            </a>
          );
        },

        // Clickable numbered citation superscripts (from preprocessed <sup data-cite="N">)
        sup: ({ children, 'data-cite': dataCite, ...props }) => {
          const citeNum = dataCite ? parseInt(dataCite, 10) : null;
          if (citeNum && onInlineCitationClick) {
            return (
              <sup
                role="button"
                tabIndex={0}
                onClick={() => onInlineCitationClick(citeNum)}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onInlineCitationClick(citeNum); } }}
                aria-label={`Citation ${citeNum}`}
                style={{
                  color: '#0073bb',
                  fontWeight: 600,
                  cursor: 'pointer',
                  fontSize: '0.75em',
                  padding: '0 1px',
                }}
                {...props}
              >
                {children}
              </sup>
            );
          }
          return <sup {...props}>{children}</sup>;
        },

        // Custom styling for strong/bold text
        strong: ({ children }) => (
          <strong style={{
            fontWeight: 600,
          }}>
            {children}
          </strong>
        ),

        // Custom styling for emphasis/italic text
        em: ({ children }) => (
          <em style={{
            fontStyle: 'italic',
          }}>
            {children}
          </em>
        ),

        // Table overrides for professional styling
        table: ({ children }) => (
          <div style={ts.wrapper}>
            <table style={ts.table}>{children}</table>
          </div>
        ),
        thead: ({ children }) => (
          <thead style={ts.thead}>{children}</thead>
        ),
        th: ({ children }) => (
          <th style={ts.th}>{children}</th>
        ),
        tbody: ({ children }) => {
          rowIndex = 0;
          return <tbody>{children}</tbody>;
        },
        tr: ({ children, isHeader }) => {
          if (isHeader) return <tr>{children}</tr>;
          const idx = rowIndex++;
          const style = idx % 2 === 1 ? ts.trAlt : {};
          return <tr style={style}>{children}</tr>;
        },
        td: ({ children }) => {
          const text = typeof children === 'string' ? children :
            Array.isArray(children) ? children.map(c => typeof c === 'string' ? c : '').join('') : '';
          const style = isNumericContent(text) ? ts.tdNumeric : ts.td;
          return <td style={style}>{children}</td>;
        }
      }}
    >
      {cleanText}
    </ReactMarkdown>
  );
};

function ReviewPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const { isDark, isSummit } = useTheme();
  const tabEnteredAt = React.useRef(Date.now());
  const [analysisResults, setAnalysisResults] = useState([]);
  const [sessionData, setSessionData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('results');
  const [insightsCompleted, setInsightsCompleted] = useState(false);

  // Fire demo_completed when ReviewPage receives actual results.
  // Depends on location.state so it fires when ProcessPage navigates here with data.
  const demoCompletedFired = React.useRef(false);
  useEffect(() => {
    const hasRouterData = !!(location.state && location.state.data);
    const hasStorageData = !!resultsCache.load();
    if ((hasRouterData || hasStorageData) && !demoCompletedFired.current) {
      demoCompletedFired.current = true;
      console.log('📊 Firing demo_completed event');
      analyticsService.trackFlowStep('demo_completed');
    }
  }, [location.state]);

  // Insights state management
  const [insightsState, setInsightsState] = useState('loading'); // loading, completed, failed, not_available
  const [insightsData, setInsightsData] = useState(null);
  const [insightsError, setInsightsError] = useState(null);
  const [insightsProgress, setInsightsProgress] = useState(0);
  const [insightsStage, setInsightsStage] = useState('Initializing insights generation...');
  const [insightsProcessingTime, setInsightsProcessingTime] = useState(0);

  // Citation deep-dive panel state
  const [citationPanel, setCitationPanel] = useState({
    isOpen: false,
    documentName: '',
    documentPath: '',
    pageNumber: 1,
    highlightText: '',
    citationText: '',
    triggerRef: null,
  });

  const handleCitationClick = (citation, triggerElement) => {
    const { documentName, documentPath } = getDocumentInfo(citation.source);
    const pageNumber = parsePageNumber(citation.page);
    setCitationPanel({
      isOpen: true,
      documentName,
      documentPath,
      pageNumber,
      highlightText: citation.fullText || citation.text,
      citationText: citation.text || '',
      triggerRef: triggerElement,
    });
  };

  const handleCitationPanelClose = () => {
    setCitationPanel(prev => {
      if (prev.triggerRef) prev.triggerRef.focus();
      return { ...prev, isOpen: false };
    });
  };

  useEffect(() => {
    try {
      let data = null;

      // Load data from sessionStorage (primary path — ProcessPage writes here before navigating)
      data = resultsCache.load();
      if (data) {
        console.log('Loading data from resultsCache (sessionStorage)');
      }

      if (!data) {
        // Distinguish "never ran" from "results lost"
        const hadSession = sessionStorage.getItem('processingComplete') === 'true'
          || location.state?.sessionId;
        if (hadSession) {
          setError('Your results could not be loaded. Try navigating back from the Process page, or start a new analysis.');
        } else {
          setError('No research results found. Please start a new analysis.');
        }
        setLoading(false);
        return;
      }

      console.log('Processing data:', data);
      console.log('Data structure:', {
        hasResults: !!data.results,
        resultsLength: data.results ? data.results.length : 0,
        resultsType: typeof data.results,
        isArray: Array.isArray(data.results),
        firstResult: data.results && data.results[0] ? Object.keys(data.results[0]) : null
      });

      setSessionData(data);

      // Ensure results is an array before mapping
      const results = data.results || [];
      if (!Array.isArray(results)) {
        console.error('Results is not an array:', results);
        setError('Invalid results data format.');
        setLoading(false);
        return;
      }

      // Transform results for display - handle the actual data structure from Graph Engine
      const transformedResults = results.map((result, index) => {
        // Extract the final answer from citation_tracking result (which contains the formatted report)
        const citationResult = result.research_documents?.citation_tracking?.result || '';
        const documentResult = result.research_documents?.document_research?.result || '';

        // Debug logging for the first result
        if (index === 0) {
          console.log('First result structure:', {
            question_id: result.question_id,
            hasDocumentResult: !!documentResult,
            documentResultLength: documentResult.length,
            documentResultPreview: documentResult.substring(0, 200),
            hasCitationResult: !!citationResult,
            citationResultLength: citationResult.length,
            citationResultPreview: citationResult.substring(0, 200)
          });
        }

        // Try to extract a clean answer from the results
        let cleanAnswer = '';

        // First priority: Use citation_tracking result (final polished report)
        if (citationResult) {
          // Use the full citation result but clean it up for display
          // Remove references section and clean up formatting
          let fullContent = citationResult;

          // Remove the references section
          fullContent = fullContent.replace(/---\s*## References[\s\S]*$/m, '');

          // Remove any trailing horizontal rules
          fullContent = fullContent.replace(/---+\s*$/m, '');

          // Clean up blockquote markers from Executive Summary
          fullContent = fullContent.replace(/## Executive Summary\s*>\s*([^\n]+)/m, '## Executive Summary\n\n$1');

          // Use the cleaned full content
          cleanAnswer = fullContent.trim();

          // If the content is too short, try to extract specific sections
          if (cleanAnswer.length < 100) {
            // Try to extract Key Findings section
            const keyFindingsMatch = citationResult.match(/## Key Findings\s*([\s\S]*?)(?=---\s*## References|$)/);
            if (keyFindingsMatch) {
              cleanAnswer = '## Key Findings\n\n' + keyFindingsMatch[1].trim();
            } else {
              // Final fallback: get everything except references
              const withoutReferences = citationResult.replace(/---\s*## References[\s\S]*$/, '').trim();
              if (withoutReferences.length > 50) {
                cleanAnswer = withoutReferences;
              }
            }
          }
        }

        // Second priority: Use document_research result if citation_tracking is not available
        if (!cleanAnswer && documentResult) {
          // Extract the main answer from document research, removing source attribution
          const lines = documentResult.split('\n');
          const answerLines = [];

          for (const line of lines) {
            if (line.includes('Source:') || line.includes('Page:') || line.includes('**Source Attribution:**')) {
              break; // Stop at source attribution
            }
            if (line.trim() && !line.includes('**Key Supporting Quote:**')) {
              answerLines.push(line.trim());
            }
          }

          cleanAnswer = answerLines.join(' ').trim();
        }

        // Final fallback - use any available result
        if (!cleanAnswer) {
          cleanAnswer = citationResult || documentResult || 'No answer available';
        }

        // Map question IDs to actual question text
        const questionMap = {
          'q_1': "What was Amazon's total revenue in 2024?",
          'q_2': "How does Amazon address competitive threats and maintain its market position across different business segments?",
          'q_3': "What are Amazon's key risk factors mentioned in their 2024 annual report?",
          'q_4': "What is Amazon's strategy for AWS growth and market expansion?",
          'q_5': "How does Amazon approach ESG (Environmental, Social, Governance) commitments?",
          'q_6': "What are Amazon's major capital expenditures and investment priorities?"
        };

        const questionText = questionMap[result.question_id] || `Question ${index + 1}`;

        // Enhanced citation extraction from multiple sources and formats
        const citations = extractCitations(documentResult, citationResult, result, questionText);

        return {
          question: questionText,
          answer: cleanAnswer || 'No answer available',
          status: result.status || 'unknown',
          confidence: 0.85, // Default confidence since it's not in the top level
          executionTime: result.execution_time || 0,
          citations: citations
        };
      });

      setAnalysisResults(transformedResults);
      setLoading(false);

      // Start insights polling immediately if we have a session ID
      if (data.session_id) {
        startInsightsPolling(data.session_id);
      } else {
        setInsightsState('not_available');
      }

    } catch (err) {
      console.error('Error processing results data:', err);
      setError(`Error loading results: ${err.message}`);
      setLoading(false);
      setInsightsState('not_available');
    }
  }, [location.state]);

  const getConfidenceBadge = (confidence) => {
    const confidencePercent = Math.round(confidence * 100);
    if (confidencePercent >= 80) return <Badge color="green">High Confidence ({confidencePercent}%)</Badge>;
    if (confidencePercent >= 60) return <Badge color="blue">Medium Confidence ({confidencePercent}%)</Badge>;
    return <Badge color="grey">Low Confidence ({confidencePercent}%)</Badge>;
  };

  const getStatusBadge = (status) => {
    if (status === 'completed') return <Badge color="green">Completed</Badge>;
    if (status === 'timeout') return <Badge color="red">Timeout</Badge>;
    return <Badge color="grey">{status}</Badge>;
  };

  const handleInsightsCompleted = (insightsData) => {
    console.log('✅ Insights completed:', insightsData);
    setInsightsCompleted(true);
    // Note: Not auto-switching to insights tab - let user choose when to view
  };

  // Insights polling function (moved from InsightsSection)
  const startInsightsPolling = async (sessionId) => {
    if (!sessionId) {
      setInsightsState('not_available');
      return;
    }

    let startTime = Date.now();

    try {
      console.log(`🔄 Starting insights polling for session ${sessionId}`);

      // Add initial delay to let research complete and insights start
      setInsightsStage('Waiting for research to complete...');
      await new Promise(resolve => setTimeout(resolve, 5000)); // 5 second delay

      setInsightsStage('Starting insights generation...');
      setInsightsProgress(10);

      // Update processing time every second
      const timeInterval = setInterval(() => {
        setInsightsProcessingTime(Math.round((Date.now() - startTime) / 1000));
      }, 1000);

      // Use the simplified polling method
      const result = await apiService.pollForInsights(sessionId, 60, 3000); // 3 minute timeout

      // Clear the time interval
      clearInterval(timeInterval);

      if (result.success && result.results) {
        console.log('✅ Insights completed successfully:', result.results);

        setInsightsData(result.results);
        setInsightsState('completed');
        setInsightsProgress(100);
        setInsightsStage('Insights generation completed');

        // Trigger the completion handler
        handleInsightsCompleted(result.results);
      } else {
        console.log('❌ Insights polling failed:', result.error);
        setInsightsError(result.error || 'Insights generation failed');
        setInsightsState('failed');
        clearInterval(timeInterval);
      }

    } catch (error) {
      console.error('❌ Error in insights polling:', error);
      setInsightsError(`Insights generation error: ${error.message}`);
      setInsightsState('failed');
    }
  };

  if (loading) {
    return (
      <Container>
        <SpaceBetween size="l">
          <Header variant="h1">Loading Results</Header>
          <Alert type="info">Loading analysis results...</Alert>
        </SpaceBetween>
      </Container>
    );
  }

  if (error) {
    return (
      <Container>
        <SpaceBetween size="l">
          <Header variant="h1">Error</Header>
          <Alert type="error">{error}</Alert>
          <Button variant="primary" onClick={() => window.location.href = '/v2/process'}>
            Start New Analysis
          </Button>
        </SpaceBetween>
      </Container>
    );
  }

  return (
    <Container>
      <SpaceBetween size="l">
        <Header
          variant="h1"
          actions={
            <SpaceBetween direction="horizontal" size="s">
              {sessionData?.session_id && (
                <Hotspot hotspotId={HOTSPOT_IDS.reviewReport} direction="bottom">
                  <Button 
                    variant="primary" 
                    iconName="gen-ai"
                    onClick={() => navigate(`/v2/report/${sessionData.session_id}`)}
                  >
                    Generate Executive Brief
                  </Button>
                </Hotspot>
              )}
              <Button variant="normal" onClick={() => window.location.href = '/v2/process'}>
                Start New Analysis
              </Button>
            </SpaceBetween>
          }
        >
          <img 
            src="/images/logo/researchAI_Logo.png" 
            alt="ResearchAI" 
            style={{ height: '40px', width: '40px', marginRight: '16px', verticalAlign: 'middle' }}
          />
          Analysis Results
        </Header>

        <SpaceBetween size="s">
          <Alert type="success">
            Analysis completed! {sessionData?.completed_questions || analysisResults.length} of {sessionData?.total_questions || analysisResults.length} questions processed successfully.
            {sessionData && (
              <div style={{ marginTop: '8px' }}>
                <strong>Execution Time:</strong> {Math.round(sessionData.overall_execution_time || 0)}s |
                <strong> Success Rate:</strong> {Math.round((sessionData.overall_success_rate || 0) * 100)}% |
                <strong> Total Questions:</strong> {sessionData.total_questions || 0}
              </div>
            )}
          </Alert>

          {sessionData?.session_id && insightsState === 'loading' && (
            <Alert type="info" dismissible>
              🤖 <strong>AI Insights Generation Started</strong> - Our AI agents are analyzing your research results to generate strategic insights. Current stage: {insightsStage}
            </Alert>
          )}

          {insightsCompleted && (
            <Alert
              type="success"
              dismissible
              action={
                <button
                  type="button"
                  onClick={() => setActiveTab('insights')}
                  style={{
                    background: 'linear-gradient(135deg, #0972d3, #44b9d6)',
                    color: '#fff',
                    border: 'none',
                    borderRadius: '8px',
                    padding: '8px 20px',
                    fontSize: '14px',
                    fontWeight: 600,
                    cursor: 'pointer',
                    whiteSpace: 'nowrap',
                  }}
                >
                  View Insights
                </button>
              }
            >
              🎉 <strong>AI Strategic Insights Ready!</strong> Our AI has analyzed your research results and generated strategic insights and recommendations.
            </Alert>
          )}
        </SpaceBetween>

        <Tabs
          activeTabId={activeTab}
          onChange={({ detail }) => {
            const tab = detail.activeTabId;
            setActiveTab(tab);
            // Track optional module visits
            if (tab === 'guardrails') analyticsService.trackModuleVisit('guardrails');
            if (tab === 'insights') analyticsService.trackModuleVisit('insights');
            analyticsService.trackTimeOnSection('review', activeTab, Date.now() - tabEnteredAt.current);
            tabEnteredAt.current = Date.now();
          }}
          tabs={[
            {
              id: 'results',
              label: (
                <>
                  Research Results
                  <Hotspot hotspotId={HOTSPOT_IDS.reviewAnswers} direction="bottom" />
                </>
              ),
              content: (
                <SpaceBetween size="m">
                  {analysisResults.map((result, index) => (
                    <ExpandableSection
                      key={index}
                      headerText={`Q${index + 1}: ${result.question}`}
                      headerActions={
                        <SpaceBetween direction="horizontal" size="xs">
                          {getStatusBadge(result.status)}
                        </SpaceBetween>
                      }
                      defaultExpanded={index === 0}
                    >
                      <SpaceBetween size="m">
                        <ChatBubble
                          ariaLabel={`ResearchAI answer for question ${index + 1}`}
                          type="incoming"
                          actions={
                            <ButtonGroup
                              ariaLabel="Chat bubble actions"
                              variant="icon"
                              items={[
                                {
                                  type: "icon-button",
                                  id: "copy",
                                  iconName: "copy",
                                  text: "Copy",
                                  popoverFeedback: (
                                    <StatusIndicator type="success">
                                      Answer copied
                                    </StatusIndicator>
                                  )
                                }
                              ]}
                            />
                          }
                          avatar={
                            <img 
                              src="/images/logo/researchAI_Logo.png" 
                              alt="ResearchAI" 
                              style={{ 
                                width: '30px', 
                                height: '30px', 
                                objectFit: 'cover'
                              }}
                            />
                          }
                        >
                          <div style={{ lineHeight: '1.6' }}>
                            {renderMarkdown(result.answer, result.citations, (citationNum) => {
                              // Scroll to the citation card and open the PDF panel
                              const citation = result.citations[citationNum - 1];
                              if (citation) {
                                handleCitationClick(citation, document.activeElement);
                              }
                            }, isDark, isSummit)}
                          </div>
                          {result.executionTime && (
                            <Box marginTop="s">
                              <TextContent>
                                <p style={{ fontSize: '12px', color: 'inherit', opacity: 0.7 }}>
                                  <strong>Execution Time:</strong> {Math.round(result.executionTime)}s
                                </p>
                              </TextContent>
                            </Box>
                          )}
                        </ChatBubble>

                        {result.citations && result.citations.length > 0 && (
                          <ExpandableSection
                            headerText={`Citations & Sources (${result.citations.length})`}
                            defaultExpanded={false}
                            variant="container"
                          >
                            <CitationPanel
                              citations={result.citations}
                              onCitationClick={(citation) => {
                                handleCitationClick(citation, document.activeElement);
                              }}
                            />
                          </ExpandableSection>
                        )}
                      </SpaceBetween>
                    </ExpandableSection>
                  ))}
                </SpaceBetween>
              )
            },
            {
              id: 'insights',
              label: insightsCompleted ? (
                <>
                  <img 
                    src="/images/logo/researchAI_Logo.png" 
                    alt="ResearchAI" 
                    style={{ 
                      width: '20px', 
                      height: '20px', 
                      marginRight: '8px',
                      verticalAlign: 'middle'
                    }}
                  />
                  AI Strategic Insights ✨
                  <Hotspot hotspotId={HOTSPOT_IDS.reviewInsightsTab} direction="bottom" />
                </>
              ) : (
                <>
                  <img 
                    src="/images/logo/researchAI_Logo.png" 
                    alt="ResearchAI" 
                    style={{ 
                      width: '20px', 
                      height: '20px', 
                      marginRight: '8px',
                      verticalAlign: 'middle'
                    }}
                  />
                  AI Strategic Insights
                  <Hotspot hotspotId={HOTSPOT_IDS.reviewInsightsTab} direction="bottom" />
                </>
              ),
              content: (
                <InsightsSection
                  insightsState={insightsState}
                  insightsData={insightsData}
                  error={insightsError}
                  progress={insightsProgress}
                  currentStage={insightsStage}
                  processingTime={insightsProcessingTime}
                />
              )
            },
            {
              id: 'whats-next',
              label: (
                <>
                  What's Next
                  <Hotspot hotspotId={HOTSPOT_IDS.reviewWhatsNextTab} direction="bottom" />
                </>
              ),
              content: (
                <SpaceBetween size="m">
                  <WhatsNextSection />
                  <FeedbackWidget context="research_results" />
                </SpaceBetween>
              )
            },
            {
              id: 'guardrails',
              label: '🛡️ Guardrails',
              content: <GuardrailsShowcase />
            }
          ]}
        />
      </SpaceBetween>
      <CitationSidePanel
        isOpen={citationPanel.isOpen}
        documentName={citationPanel.documentName}
        documentPath={citationPanel.documentPath}
        pageNumber={citationPanel.pageNumber}
        highlightText={citationPanel.highlightText}
        citationText={citationPanel.citationText}
        onClose={handleCitationPanelClose}
      />
    </Container>
  );
}

export default ReviewPage;
